import React, { useState, useEffect, useRef } from 'react';
import { Bell, Search, User, LogOut, Settings, Shield, ChevronDown, Menu, X, Sun, Moon, Award } from 'lucide-react';
import { AvatarWithBadges } from './common/AvatarWithBadges';
import { BadgeManager } from './common/BadgeManager';

interface HeaderProps {
  currentUser: any;
  onLogout: () => void;
  onOpenProfile: () => void;
  onToggleMobileMenu: () => void;
  darkMode?: boolean;
  toggleDarkMode?: () => void;
  onNavigate?: (view: string) => void;
}

export const Header: React.FC<HeaderProps> = ({ 
  currentUser, 
  onLogout, 
  onOpenProfile,
  onToggleMobileMenu,
  darkMode = false,
  toggleDarkMode,
  onNavigate
}) => {
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showBadgeManager, setShowBadgeManager] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when search is shown
  useEffect(() => {
    if (showSearch && searchRef.current) {
      searchRef.current.focus();
    }
  }, [showSearch]);

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_id');
    onLogout();
    setShowUserDropdown(false);
  };

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'healthcare_provider': return 'Healthcare Provider';
      case 'lab_technician': return 'Lab Technician';
      case 'demo': return 'Demo User';
      default: return 'Patient';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'super_admin': return 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400';
      case 'healthcare_provider': return 'text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400';
      case 'lab_technician': return 'text-purple-600 bg-purple-50 dark:bg-purple-900/20 dark:text-purple-400';
      case 'demo': return 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400';
      default: return 'text-slate-600 bg-slate-50 dark:bg-slate-900/20 dark:text-slate-400';
    }
  };

  const handleProfileClick = () => {
    onOpenProfile();
    setShowUserDropdown(false);
  };

  const handleAccountSettingsClick = () => {
    if (onNavigate) {
      onNavigate('settings');
    }
    setShowUserDropdown(false);
  };

  const handleSecurityPrivacyClick = () => {
    if (onNavigate) {
      onNavigate('security');
    }
    setShowUserDropdown(false);
  };

  const handleBadgesClick = () => {
    setShowBadgeManager(true);
    setShowUserDropdown(false);
  };

  // Get user display name with fallback
  const getUserDisplayName = () => {
    if (!currentUser) return 'Guest User';
    
    const firstName = currentUser.first_name || currentUser.firstName || '';
    const lastName = currentUser.last_name || currentUser.lastName || '';
    
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }
    
    return currentUser.email ? currentUser.email.split('@')[0] : 'Guest User';
  };

  return (
    <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 py-3 lg:px-6">
      <div className="flex items-center justify-between">
        {/* Mobile Menu Button */}
        <button
          onClick={onToggleMobileMenu}
          className="lg:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          aria-label="Toggle mobile menu"
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* Search Bar */}
        <div className="flex-1 max-w-md mx-4 hidden sm:block">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search health data, medications, symptoms..."
              className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Mobile Search Toggle */}
        <button
          onClick={() => setShowSearch(!showSearch)}
          className="sm:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          aria-label="Toggle search"
        >
          <Search className="h-5 w-5" />
        </button>

        {/* Mobile Search Bar */}
        {showSearch && (
          <div className="absolute top-full left-0 right-0 p-4 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 sm:hidden">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={() => setShowSearch(false)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1"
              >
                <X className="h-4 w-4 text-slate-400" />
              </button>
            </div>
          </div>
        )}

        {/* Right Side Actions */}
        <div className="flex items-center space-x-4">
          {/* Dark Mode Toggle */}
          <button
            onClick={toggleDarkMode}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            aria-label="Toggle dark mode"
          >
            {darkMode ? (
              <Sun className="h-5 w-5 text-slate-600 dark:text-slate-400" />
            ) : (
              <Moon className="h-5 w-5 text-slate-600 dark:text-slate-400" />
            )}
          </button>

          {/* Notifications */}
          <button className="relative p-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors hidden sm:block">
            <Bell className="h-5 w-5" />
            <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
              3
            </span>
          </button>
          
          {/* User Profile Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowUserDropdown(!showUserDropdown)}
              className="flex items-center space-x-2 md:space-x-3 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors btn-interactive"
              aria-label="User menu"
              aria-expanded={showUserDropdown}
            >
              <AvatarWithBadges 
                user={currentUser} 
                size="sm" 
                showBadges={false}
                showFrames={true}
                showIndicators={true}
                className="w-8 h-8"
              />
              <div className="hidden sm:block text-left">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  {getUserDisplayName()}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  ZippyHealth Member #{currentUser?.id?.slice(-5) || '00000'}
                </p>
              </div>
              <ChevronDown className={`h-4 w-4 text-slate-500 dark:text-slate-400 transition-transform ${showUserDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showUserDropdown && (
              <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 py-2 z-50 max-w-[calc(100vw-2rem)]">
                <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
                  <p className="text-sm font-medium text-slate-800 dark:text-white">
                    {getUserDisplayName()}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{currentUser?.email || 'No email'}</p>
                  {currentUser?.role && (
                    <span className={`inline-block mt-2 px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(currentUser.role)}`}>
                      {getRoleDisplayName(currentUser.role)}
                    </span>
                  )}
                </div>
                
                <div className="py-1">
                  <button
                    onClick={handleProfileClick}
                    className="w-full flex items-center space-x-3 px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                  >
                    <User className="h-4 w-4" />
                    <span>View Profile</span>
                  </button>
                  
                  <button
                    onClick={handleBadgesClick}
                    className="w-full flex items-center space-x-3 px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                  >
                    <Award className="h-4 w-4" />
                    <span>Badges & Achievements</span>
                  </button>
                  
                  <button
                    onClick={handleAccountSettingsClick}
                    className="w-full flex items-center space-x-3 px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    <span>Account Settings</span>
                  </button>
                  
                  <button
                    onClick={handleSecurityPrivacyClick}
                    className="w-full flex items-center space-x-3 px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                  >
                    <Shield className="h-4 w-4" />
                    <span>Security & Privacy</span>
                  </button>
                </div>

                <div className="border-t border-slate-200 dark:border-slate-700 py-1">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center space-x-3 px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Badge Manager Modal */}
      {showBadgeManager && (
        <BadgeManager 
          user={currentUser} 
          onClose={() => setShowBadgeManager(false)} 
        />
      )}
    </header>
  );
};
